// RegisterIsometric - a technical view's runs on the exploded levels, from
// below the slab to the roof (guide 18.6). Context layers are faint
// (rooms, the pathways of the requested systems, connected devices); the
// requested runs are strong. Risers are vertical lines between the levels
// they connect. Every run is one visible path plus a wide transparent hit
// path, so a thin sleeve is still easy to hover.
import React, { useMemo } from "react";
import { BELOW, ROOF, levelName, runColor, runDash, runWidth } from "./viewModel";
import { bounds, makeStackProjector, pointsAttr, polysPath, sortedLevels } from "../glanceModel";

export default function RegisterIsometric({ manifest, view, visibleIds, highlightId, onHover, onSelect, thumbnail = false }) {
  const levels = useMemo(() => sortedLevels(manifest), [manifest]);
  const b = useMemo(() => bounds(levels), [levels]);
  const hasBelow = view.rows.some((r) => r.level === BELOW || r.level2 === BELOW);
  const hasRoof = view.rows.some((r) => r.level === ROOF || r.level2 === ROOF) || (view.anchors || []).some((a) => a.level === ROOF);
  const plates = [...(hasBelow ? [BELOW] : []), ...levels.map((l) => l.id), ...(hasRoof ? [ROOF] : [])];
  const { project, height, width } = useMemo(() => makeStackProjector(b, plates.length, { width: thumbnail ? 1140 : 1280 }), [b, plates.length, thumbnail]);
  const rank = (id) => plates.indexOf(id) + 1;
  const outline = (id) => (id === BELOW ? levels[0] : id === ROOF ? levels[levels.length - 1] : levels.find((l) => l.id === id)) || {};
  const contextSystems = new Set((view.context && view.context.systems) || []);
  const showRooms = !view.request || !view.request.context || view.request.context.includes("rooms") || view.source === "import";
  const showDevices = !view.request || !view.request.context || view.request.context.includes("devices") || view.source === "import";

  const lift = (id) => (id === BELOW ? 0 : 6);
  const P = (x, y, id, extra = 0) => { const p = project(x, y, rank(id)); return [p[0], p[1] - lift(id) - extra]; };

  return (
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`Isometric of ${view.title} on each level`}>
      {plates.map((id) => {
        const lvl = outline(id);
        const r = rank(id);
        const lp = project(b.xmin, b.ymin, r);
        const pathways = [];
        if (id !== BELOW && id !== ROOF) {
          Object.entries(manifest.buckets || {}).forEach(([k, bucket]) => {
            const [l, s] = k.split("|");
            if (l === id && contextSystems.has(s) && bucket.polys) pathways.push(polysPath(bucket.polys, (x, y) => project(x, y, r)));
          });
        }
        return (
          <g key={id}>
            {lvl.footprint && (
              <polygon points={pointsAttr(lvl.footprint, (p) => project(p[0], p[1], r))}
                       fill={id === BELOW ? "none" : id === ROOF ? "var(--roof)" : "var(--sheet)"}
                       stroke={id === BELOW ? "var(--line)" : "var(--ink)"} strokeWidth={id === BELOW ? 1 : 1.3}
                       strokeDasharray={id === BELOW || id === ROOF ? "6 5" : undefined} />
            )}
            {showRooms && id !== BELOW && id !== ROOF && (lvl.rooms || []).filter((rm) => rm.category !== "circ").map((rm) => (
              <polygon key={rm.id} points={pointsAttr(rm.polygon, (p) => project(p[0], p[1], r))} fill="var(--room)" stroke="var(--line)" strokeWidth="0.35" />
            ))}
            {pathways.map((d, n) => <path key={n} d={d} fill="var(--path)" stroke="var(--pathink)" strokeWidth="0.3" opacity="0.85" />)}
            {!thumbnail && <text x={lp[0] + 2} y={lp[1] + 22} fontSize="17" fontWeight="600" fill="var(--ink)">{levelName(manifest, id)}</text>}
          </g>
        );
      })}

      {showDevices && (view.anchors || []).filter((a) => plates.includes(a.level)).map((a, n) => {
        const p = P(a.x, a.y, a.level);
        return (
          <g key={`a-${n}`} style={{ pointerEvents: "none" }}>
            <rect x={p[0] - 9} y={p[1] - 14} width="18" height="14" fill="var(--system)" stroke="var(--systemink)" />
            {/acp/i.test(a.kind || "") && <rect x={p[0] + 11} y={p[1] - 11} width="9" height="9" fill="var(--sec)" />}
            {!thumbnail && (
              <text x={p[0] + 24} y={p[1] - 16} fontSize="13" fontWeight="600" fill="var(--ink)" paintOrder="stroke" stroke="var(--sheet)" strokeWidth="4">{a.name}</text>
            )}
          </g>
        );
      })}

      {view.rows.map((r) => {
        if (!r.geom || !r.geom.length || r.level == null || !plates.includes(r.level)) return null;
        if (visibleIds && !visibleIds.has(r.id)) return null;
        const on = highlightId === r.id;
        const color = on ? "var(--hi)" : runColor(r);
        let d;
        if (r.level2 && plates.includes(r.level2)) {
          const a = P(r.geom[0][0], r.geom[0][1], r.level, 8);
          const last = r.geom[r.geom.length - 1];
          const z = P(last[0], last[1], r.level2);
          d = `M${a[0].toFixed(1)} ${a[1].toFixed(1)}L${z[0].toFixed(1)} ${z[1].toFixed(1)}`;
        } else if (r.geom.length === 1) {
          const p = P(r.geom[0][0], r.geom[0][1], r.level);
          d = `M${(p[0] - 3.5).toFixed(1)} ${p[1].toFixed(1)}a3.5 3.5 0 1 0 7 0a3.5 3.5 0 1 0 -7 0`;
        } else {
          d = r.geom.map((q, i) => { const p = P(q[0], q[1], r.level); return `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`; }).join("");
        }
        const point = r.geom.length === 1 && !r.level2;
        return (
          <g key={r.id} className="g-run">
            <path d={d} fill={point ? color : "none"} stroke={color} strokeWidth={on ? runWidth(r) + 2 : runWidth(r)}
                  strokeLinecap="round" strokeDasharray={runDash(r)} />
            {!thumbnail && (
              <path d={d} fill="none" stroke="transparent" strokeWidth="14" style={{ cursor: "pointer" }}
                    onMouseEnter={(e) => onHover && onHover(r.id, e)} onMouseMove={(e) => onHover && onHover(r.id, e)}
                    onMouseLeave={() => onHover && onHover(null)} onClick={() => onSelect && onSelect(r.id)}>
                <title>{`${r.id} ${r.type}`}</title>
              </path>
            )}
          </g>
        );
      })}
    </svg>
  );
}
